/**
 * Unified Tenant Selector Component
 *
 * Purpose: Universal tenant selector component that works with both Metadata Manager
 *          and Master Data Manager contexts. Supports both simple string arrays and
 *          complex tenant objects with permissions.
 *
 * Architecture: React component with flexible data structure support
 * Dependencies: React, Material-UI, common types
 * Author: Development Team
 * Created: 2025-01-27
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { Public as GlobalIcon, Business as TenantIcon } from '@mui/icons-material';
import { TenantChip } from './StyledComponents';

// Type definitions for different tenant data structures
export interface SimpleTenant {
  tenant_id: string;
  name?: string;
}

export interface ComplexTenant {
  tenant_id: string;
  name: string;
  is_global: boolean;
  can_edit: boolean;
}

export type TenantData = string[] | SimpleTenant[] | ComplexTenant[];

// Props interface
export interface TenantSelectorProps {
  currentTenant: string;
  availableTenants: TenantData;
  onTenantChange: (tenantId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  showIcons?: boolean;
  showChips?: boolean;
  dataTestId?: string;
}

/**
 * Unified Tenant Selector Component
 *
 * Purpose: Provides consistent tenant selection across different contexts
 * Features: Supports both simple and complex tenant data structures
 */
const TenantSelector: React.FC<TenantSelectorProps> = ({
  currentTenant,
  availableTenants,
  onTenantChange,
  disabled = false,
  loading = false,
  size = 'medium',
  label = 'Tenant Context',
  showIcons = true,
  showChips = true,
  dataTestId = 'tenant-context-selector',
}) => {
  /**
   * Handle tenant selection change
   */
  const handleChange = (event: SelectChangeEvent<string>) => {
    const newTenantId = event.target.value;
    if (newTenantId !== currentTenant) {
      onTenantChange(newTenantId);
    }
  };

  /**
   * Get tenant display information based on data structure
   */
  const getTenantInfo = (tenant: string | SimpleTenant | ComplexTenant) => {
    if (typeof tenant === 'string') {
      // Simple string format (MDM)
      return {
        tenant_id: tenant,
        name: tenant === 'GLOBAL' ? 'Global System Admin' : `Tenant ${tenant}`,
        is_global: tenant === 'GLOBAL',
        can_edit: true,
      };
    } else if ('is_global' in tenant) {
      // Complex tenant object (Metadata Manager)
      return {
        tenant_id: tenant.tenant_id,
        name: tenant.name,
        is_global: tenant.is_global,
        can_edit: tenant.can_edit,
      };
    } else {
      // Simple tenant object
      return {
        tenant_id: tenant.tenant_id,
        name: tenant.name || `Tenant ${tenant.tenant_id}`,
        is_global: tenant.tenant_id === 'GLOBAL',
        can_edit: true,
      };
    }
  };

  /**
   * Render tenant menu item
   */
  const renderTenantMenuItem = (tenant: string | SimpleTenant | ComplexTenant) => {
    const tenantInfo = getTenantInfo(tenant);

    return (
      <MenuItem key={tenantInfo.tenant_id} value={tenantInfo.tenant_id}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showIcons &&
            (tenantInfo.is_global ? (
              <GlobalIcon fontSize="small" />
            ) : (
              <TenantIcon fontSize="small" />
            ))}

          <Typography variant="body2">{tenantInfo.name}</Typography>

          {showChips && !tenantInfo.can_edit && (
            <TenantChip label="Read Only" size="small" variant="outlined" className="readonly" />
          )}
        </Box>
      </MenuItem>
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size={size} sx={{ minWidth: 250 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={currentTenant}
          onChange={handleChange}
          disabled={disabled}
          label={label}
          data-testid={dataTestId}
        >
          {availableTenants.map(renderTenantMenuItem)}
        </Select>
      </FormControl>

      {loading && <CircularProgress size={20} />}
    </Box>
  );
};

export default TenantSelector;
